import React, { useEffect, useRef, useState } from 'react';
import Navbar from '../../components/Navbar/Navbar';
import Hero from '../../components/Hero/Hero';
import BrightTomorrow from '../../components/BrightTomorrow/BrightTomorrow';
import Providers from '../../components/Providers/Providers';
import ScrollStack, { ScrollStackItem } from '../../components/ScrollStack/ScrollStack';
import ScrollToTop from '../../components/ScrollToTop/ScrollToTop';
import './Landing.css';
import Footer from '../../components/Footer/Footer';

export default function Landing() {
  const scrollerRef = useRef(null);
  const [onHero, setOnHero] = useState(true);

  // Handle hash navigation
  useEffect(() => {
    const handleHashNavigation = () => {
      const hash = window.location.hash;
      if (hash) {
        // Exit hero if necessary then scroll; wait for layout to settle
        if (typeof window.enterContent === 'function') {
          window.enterContent();
        }
        setTimeout(() => {
          const element = document.getElementById(hash.substring(1));
          const scroller = scrollerRef.current;
          if (element && scroller) {
            const elementOffsetTop = element.offsetTop;
            scroller.scrollTo({ top: Math.max(0, elementOffsetTop - 80), behavior: 'smooth' });
          }
        }, 300);
      }
    };

    // Handle initial hash
    handleHashNavigation();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashNavigation);
    return () => window.removeEventListener('hashchange', handleHashNavigation);
  }, []);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    scroller.scrollTop = 0;

    // expose for CTA in Hero if needed
    window.enterContent = () => {
      setOnHero(false);
      setTimeout(() => {
        scroller.scrollTo({ top: 0, behavior: 'smooth' });
      }, 50);
    };

    // Listen for scroll to hero event from ScrollToTop component
    const handleScrollToHero = () => {
      setOnHero(true);
      setTimeout(() => {
        scroller.scrollTo({ top: 0, behavior: 'smooth' });
      }, 50);
    };

    window.addEventListener('scrollToHero', handleScrollToHero);

    const wheelOnWindow = (e) => {
      if (!onHero) return;
      if (e.deltaY > 0) {
        e.preventDefault();
        window.enterContent();
      }
    };

    const wheelOnScroller = (e) => {
      if (onHero) {
        e.preventDefault();
        return;
      }
      if (scroller.scrollTop <= 0 && e.deltaY < 0) {
        e.preventDefault();
        setOnHero(true);
        setTimeout(() => {
          scroller.scrollTo({ top: 0, behavior: 'smooth' });
        }, 50);
      }
    };

    const keyHandler = (e) => {
      const upKeys = ['PageUp', 'ArrowUp'];
      const downKeys = ['PageDown', 'ArrowDown', ' ', 'Space'];
      if (onHero && downKeys.includes(e.key)) {
        e.preventDefault();
        window.enterContent();
      } else if (!onHero && scroller.scrollTop <= 0 && upKeys.includes(e.key)) {
        e.preventDefault();
        setOnHero(true);
        setTimeout(() => {
          scroller.scrollTo({ top: 0, behavior: 'smooth' });
        }, 50);
      }
    };

    // touch helpers
    let touchStartY = 0, touchStartX = 0, touchActive = false, touchMoved = false;

    const touchStart = (e) => {
      touchActive = true;
      touchMoved = false;
      touchStartY = e.touches[0].clientY;
      touchStartX = e.touches[0].clientX;
    };
    const touchMove = (e) => {
      if (!touchActive) return;
      const currentY = e.touches[0].clientY;
      const currentX = e.touches[0].clientX;
      const dy = currentY - touchStartY;
      const dx = Math.abs(currentX - touchStartX);
      if (Math.abs(dy) > dx && Math.abs(dy) > 10) {
        touchMoved = true;
        if (onHero) {
          if (dy < 0) e.preventDefault();
        } else if (scroller.scrollTop <= 0 && dy > 0) {
          e.preventDefault();
        }
      }
    };
    const touchEnd = (e) => {
      if (!touchActive) return;
      const dy = e.changedTouches[0].clientY - touchStartY;
      const dx = Math.abs(e.changedTouches[0].clientX - touchStartX);
      if (Math.abs(dy) > dx && touchMoved) {
        if (onHero && dy < -30) {
          window.enterContent();
        } else if (!onHero && scroller.scrollTop <= 0 && dy > 30) {
          setOnHero(true);
          setTimeout(() => {
            scroller.scrollTo({ top: 0, behavior: 'smooth' });
          }, 50);
        }
      }
      touchActive = false;
      touchMoved = false;
    };

    window.addEventListener('wheel', wheelOnWindow, { passive: false });
    window.addEventListener('keydown', keyHandler);
    window.addEventListener('touchstart', touchStart, { passive: false });
    window.addEventListener('touchmove', touchMove, { passive: false });
    window.addEventListener('touchend', touchEnd, { passive: true });

    scroller.addEventListener('wheel', wheelOnScroller, { passive: false });
    scroller.addEventListener('touchstart', touchStart, { passive: false });
    scroller.addEventListener('touchmove', touchMove, { passive: false });
    scroller.addEventListener('touchend', touchEnd, { passive: true });

    return () => {
      window.removeEventListener('wheel', wheelOnWindow);
      window.removeEventListener('keydown', keyHandler);
      window.removeEventListener('touchstart', touchStart);
      window.removeEventListener('touchmove', touchMove);
      window.removeEventListener('touchend', touchEnd);
      window.removeEventListener('scrollToHero', handleScrollToHero);
      scroller.removeEventListener('wheel', wheelOnScroller);
      scroller.removeEventListener('touchstart', touchStart);
      scroller.removeEventListener('touchmove', touchMove);
      scroller.removeEventListener('touchend', touchEnd);
      delete window.enterContent;
    };
  }, [onHero]);

  return (
    <div className={`landing-root ${onHero ? 'on-hero' : ''}`}>
      {onHero && <Navbar />}
      <div className={`hero-overlay ${onHero ? 'visible' : 'hidden'}`} aria-hidden={!onHero}>
        <Hero />
      </div>

      <div className={`content-scroller ${onHero ? 'hide-scrollbar' : ''}`} ref={scrollerRef}>
        <section className="bright-tomorrow-section">
          <BrightTomorrow />
        </section>

        <section className="providers-section">
          <Providers />
        </section>

        {/* ScrollStack Section */}
               {/* ScrollStack Section */}
               <section className="scroll-stack-section">
          <div className="scroll-stack-container">
            <ScrollStack
              className="landing-scroll-stack"
              /* header is now pinned inside the same viewport as cards */
              header={
                <div className="scroll-stack-header">
                  {/* <h2 className="scroll-stack-title">My Digital Schools</h2>
                  <p className="scroll-stack-subtitle">
                  Empowering schools with one-click access to all digital learning platforms.
                  </p> */}
                </div>
              }
              headerHeight={180}          // adjust to your title+subtitle height
              itemDistance={120}
              itemScale={0.04}
              itemStackDistance={28}
              stackPosition="0%"         // relative to the space BELOW the header
              baseScale={0.86}
              rotationAmount={0}
              blurAmount={0}
              externalScrollSelector={'.content-scroller'}
              onStackComplete={() => {}}
              firstCardVisible  // <-- add this (optional; default is true)

            >
              {/* --- your cards unchanged --- */}
              <ScrollStackItem itemClassName="sso-feature-card">
                <div className="feature-content">
                  <div className="feature-icon">🔐</div>
                  <div className="feature-info">
                    <h3 className="feature-title">Centralized School SSO Hub</h3>
                    <p className="feature-description">
                      A single login system that allows students and teachers to access multiple educational platforms with one secure click — no passwords or extra accounts needed.
                    </p>
                  </div>
                </div>
              </ScrollStackItem>

              <ScrollStackItem itemClassName="sso-feature-card">
                <div className="feature-content">
                  <div className="feature-icon">⚡</div>
                  <div className="feature-info">
                    <h3 className="feature-title">One Code, Many Providers</h3>
                    <div className="feature-description">
                      <ul className="feature-points">
                        <li>Code-based login for all users</li>
                        <li>Auto-sync of students, teachers, and classes</li>
                        <li>Instant access to supported platforms</li>
                        <li>Unified analytics & reporting dashboard</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </ScrollStackItem>

              <ScrollStackItem itemClassName="sso-feature-card">
                <div className="feature-content">
                  <div className="feature-icon">🌐</div>
                  <div className="feature-info">
                    <h3 className="feature-title">Connected Learning Ecosystem</h3>
                    <div className="feature-description">
                      <ul className="feature-points">
                        <li>BOOKR Class (Reading comprehension & literacy)</li>
                        <li>Highlights Library (English learning)</li>
                        <li>SAM Learning (Assessment & revision tools)</li>
                        <li>More coming soon…</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </ScrollStackItem>
{/* 
              <ScrollStackItem itemClassName="learning-platform-card">
                <div className="platform-content">
                  <div className="platform-icon">🌍</div>
                  <div className="platform-info">
                    <h3 className="platform-name">Global Languages Hub</h3>
                    <p className="platform-description">
                      Learn languages with native speakers through immersive conversation and cultural exchange.
                    </p>
                    <div className="platform-stats">
                      <span className="platform-students">30K+ Students</span>
                      <span className="platform-rating">4.6 ⭐</span>
                    </div>
                  </div>
                </div>
              </ScrollStackItem>

              <ScrollStackItem itemClassName="learning-platform-card">
                <div className="platform-content">
                  <div className="platform-icon">🧠</div>
                  <div className="platform-info">
                    <h3 className="platform-name">Mind & Science Institute</h3>
                    <p className="platform-description">
                      Advanced courses in psychology, neuroscience, and cognitive science research.
                    </p>
                    <div className="platform-stats">
                      <span className="platform-students">12K+ Students</span>
                      <span className="platform-rating">4.9 ⭐</span>
                    </div>
                  </div>
                </div>
              </ScrollStackItem>

              <ScrollStackItem itemClassName="learning-platform-card">
                <div className="platform-content">
                  <div className="platform-icon">📊</div>
                  <div className="platform-info">
                    <h3 className="platform-name">Data Analytics Pro</h3>
                    <p className="platform-description">
                      Master data science, machine learning, and business intelligence with real-world datasets.
                    </p>
                    <div className="platform-stats">
                      <span className="platform-students">18K+ Students</span>
                      <span className="platform-rating">4.8 ⭐</span>
                    </div>
                  </div>
                </div>
              </ScrollStackItem> */}
            </ScrollStack>
          </div>
        </section>


        <Footer />
      </div>
      <ScrollToTop />
    </div>
  );
}
